/**
 * Did this failure happen AFTER the sign-in already succeeded?
 *
 * Both Google sign-in paths call `ensureUserProfileExists` once the credential
 * exchange has completed — `signInWithGoogle` after the popup resolves, and
 * `completePendingRedirect` after `getRedirectResult` resolves. The person is
 * authenticated by then, so a failure in that profile read is NOT a failed
 * sign-in, and telling them "sign-in failed" is a false alarm: the
 * `onAuthStateChange` subscription does its OWN profile read a moment later and
 * usually lands them on the dashboard.
 *
 * That second read is a fresh attempt, NOT a retry of this one — and if it fails
 * too, the person is stranded on /login authenticated. Acting on the tag is only
 * safe because the `onAuthStateChange` catch in `AuthContext.tsx` speaks in
 * exactly that case. The tag decides WHICH message someone gets; it must never
 * decide WHETHER they get one.
 *
 * 🔴 THAT SINK IS PROVEN TO SPEAK IN THE MAIN CLIENT ONLY. In client-tech it is
 * mute: `react-hot-toast` is absent from that app's vite `dedupe` list, so it
 * bundles a second copy whose mounted <Toaster> has no producers, and every
 * @shared toast — this sink included — lands in the other store. The condition
 * this predicate's safety rests on is therefore app-specific, and it is NOT
 * satisfied everywhere this module runs.
 *
 * So: do not make a catch chain silent on this tag without confirming the sink
 * still speaks IN THE APP YOU ARE SHIPPING TO. "A Toaster is mounted" is not
 * that confirmation — it was the exact check that missed this.
 *
 * Both sites tag the error (see `markPostAuthFailure`) and both catch chains ask
 * this predicate, so the two halves of the protocol cannot drift by a typo — a
 * raw `error.postauth` read would compile clean, keep every test green, and
 * silently never match.
 *
 * WHY A TAG AND NOT `auth.currentUser`. Ambient state answers "is ANYONE signed
 * in", which is also true for someone already signed in who starts a NEW
 * sign-in that genuinely fails — and that person must still be told. It is also
 * a module-global that `client-tech` substitutes at build time. The tag is a
 * fact recorded where it is known; the ambient read was correct only by
 * coincidence.
 *
 * 🔴 ORDERING IS LOAD-BEARING. `ensureUserProfileExists` also throws the
 * "No account found" refusal for an un-invited Google account, and that throw
 * gets tagged too — the tag is semantically false there, because that path signs
 * the user OUT before throwing. That refusal MUST be matched by its own branch
 * BEFORE this predicate is consulted, or an un-invited user is signed out,
 * bounced to /login and told nothing. Both catch chains order it that way, and
 * EACH has its own regression guard proven red by reordering that specific
 * chain — the redirect chain in `AuthContext.redirectCompletion.test.tsx`, the
 * popup chain in `AuthContext.transientGoogleAuth.test.tsx`. Both are needed:
 * an earlier version of this comment claimed coverage of "both chains" while
 * only the redirect one was pinned, and a reorder of the popup chain passed the
 * entire suite. Do not reorder either without its guard.
 */
export function isPostAuthFailure(error: unknown): boolean {
  return (error as { postAuth?: unknown } | null | undefined)?.postAuth === true;
}

/**
 * Record, at the throw site, that this failure happened after authentication.
 *
 * Tags in place and returns the SAME error. Tagging is best-effort and never
 * costs the error itself: `Object.assign` throws on a frozen or non-extensible
 * target (measured) and silently no-ops on a primitive (also measured — it boxes
 * the primitive into a discarded wrapper). Either way the original is rethrown
 * unchanged rather than being replaced by a TypeError, which would destroy the
 * diagnostic in Sentry for the very failure being explained. Untagged is the
 * safe direction — the user gets told.
 *
 * Mutating a caught error is a deliberate departure from the repo's immutability
 * rule. The alternative — wrapping in a new Error — loses `.code`, which the
 * `auth/user-disabled` and transient branches both depend on.
 */
export function markPostAuthFailure<T>(error: T): T {
  try {
    Object.assign(error as object, { postAuth: true });
  } catch {
    // Frozen / non-extensible. Fall through and rethrow as-is.
  }
  return error;
}
